import type { ReactNode } from 'react';

import type { RoutingTarget } from '../shared/index.js';

import type { Department, DirectoryUser, JobRole } from '../api/endpoints.js';
import { Picker } from './picker.js';

const NOWHERE = 'nowhere';

export function TargetPicker({
  value,
  departments,
  jobRoles,
  people,
  nowhereLabel,
  onChange,
}: {
  readonly value: RoutingTarget | null;
  readonly departments: readonly Department[];
  readonly jobRoles: readonly JobRole[];
  readonly people: readonly DirectoryUser[];
  readonly nowhereLabel: string;
  readonly onChange: (next: RoutingTarget | null) => void;
}): ReactNode {
  const person = value?.kind === 'person' ? people.find((entry) => entry.id === value.userId) : undefined;

  const jobRoleId =
    value?.kind === 'position' ? value.jobRoleId : (person?.jobRoleId ?? null);

  const role = jobRoles.find((entry) => entry.id === jobRoleId);

  const departmentId =
    value === null
      ? ''
      : value.kind === 'department'
        ? (value.departmentId ?? '')
        : (role?.departmentId ?? person?.homeDepartmentId ?? '');

  const rolesHere = jobRoles.filter((entry) => entry.isActive && entry.departmentId === departmentId);

  const peopleHere = people.filter((entry) => {
    if (entry.homeDepartmentId !== departmentId) return false;
    return jobRoleId === null || entry.jobRoleId === jobRoleId;
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Picker
        label="Department"
        title="Where it lands"
        placeholder={nowhereLabel}
        values={departmentId === '' ? [NOWHERE] : [departmentId]}
        options={[
          { value: NOWHERE, label: nowhereLabel },
          ...departments.map((entry) => ({ value: entry.id, label: entry.name })),
        ]}
        onChange={(next) => {
          const chosen = next[0];

          if (chosen === undefined || chosen === NOWHERE) {
            onChange(null);
            return;
          }

          onChange({ kind: 'department', departmentId: chosen, jobRoleId: null, userId: null });
        }}
      />

      {departmentId === '' ? null : (
        <Picker
          label="Position"
          title="Which position"
          placeholder="Whole department"
          values={jobRoleId === null ? [] : [jobRoleId]}
          options={[
            { value: '', label: 'Whole department' },
            ...rolesHere.map((entry) => ({ value: entry.id, label: entry.name })),
          ]}
          onChange={(next) => {
            const chosen = next[0];

            if (chosen === undefined || chosen === '') {
              onChange({ kind: 'department', departmentId, jobRoleId: null, userId: null });
              return;
            }

            onChange({ kind: 'position', departmentId: null, jobRoleId: chosen, userId: null });
          }}
        />
      )}

      {departmentId === '' ? null : (
        <Picker
          label="Person"
          title="Which person"
          placeholder={jobRoleId === null ? 'Anyone in it' : 'Anyone in that job'}
          values={value?.kind === 'person' && value.userId !== null ? [value.userId] : []}
          options={[
            { value: '', label: jobRoleId === null ? 'Anyone in it' : 'Anyone in that job' },
            ...peopleHere.map((entry) => ({
              value: entry.id,
              label: `${entry.firstName} ${entry.lastName}`,
              hint: entry.jobRoleName,
            })),
          ]}
          onChange={(next) => {
            const chosen = next[0];

            if (chosen === undefined || chosen === '') {
              onChange(
                jobRoleId === null
                  ? { kind: 'department', departmentId, jobRoleId: null, userId: null }
                  : { kind: 'position', departmentId: null, jobRoleId, userId: null },
              );
              return;
            }

            onChange({ kind: 'person', departmentId: null, jobRoleId: null, userId: chosen });
          }}
        />
      )}
    </div>
  );
}
